/**
 * What each billing state says and how loud it may be — pure, so the attention card and the
 * billing screen state one thing one way. `designs/Screen · Billing and Plan States.dc.html`,
 * states 3–5 and its matrix. Billing uses `warn` at most: README invariant 1.
 */
import { PLANS, graceEndsAt, type BillingState, type Tier } from "@packagepage/shared";

/** The mock's matrix tone column. Frozen: `test/billing-states.test.ts` holds it. */
export const BILLING_STATE_TONES: Readonly<Record<BillingState, "neutral" | "warn">> =
  Object.freeze({
    active: "neutral",
    grace: "warn",
    degraded: "warn",
    churned: "neutral",
  });

/** The ladder's facts as `orgs.current` answers them. */
export interface BillingStanding {
  readonly plan: Tier;
  readonly billingStatus: BillingState;
  readonly graceSince: string | null;
  readonly paymentFailure: string | null;
  readonly fairUseOver: boolean;
}

/** `22 February` — in UTC, so every viewer reads one day. */
export function dayName(at: Date): string {
  return at.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/** The first day of the next calendar month in UTC — when the fair-use count resets. */
export function windowResetsOn(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/** Stripe's codes, in a bank's words. A code not listed is shown as itself. */
const DECLINE_WORDS: Readonly<Record<string, string>> = {
  expired_card: "expired card",
  insufficient_funds: "insufficient funds",
  card_declined: "card declined",
  generic_decline: "card declined",
  do_not_honor: "do not honour",
  incorrect_cvc: "incorrect security code",
  incorrect_number: "incorrect card number",
  lost_card: "card reported lost",
  stolen_card: "card reported stolen",
  card_velocity_exceeded: "card limit reached",
  processing_error: "processing error",
  authentication_required: "authentication required",
};

/** The failed payment's reason as one sentence, or the plain fallback when Stripe gave none. */
export function paymentFailureSentence(code: string | null): string {
  if (code === null) return "The payment did not go through.";
  const words = DECLINE_WORDS[code];
  return words === undefined
    ? `The payment did not go through. Our payment provider reported "${code}".`
    : `Your bank said "${words}".`;
}

/** Whether a degraded organisation got there by an unpaid invoice — 21 days of grace. */
export function degradedByPayment(standing: BillingStanding, now: Date): boolean {
  return (
    standing.graceSince !== null &&
    now.getTime() >= graceEndsAt(new Date(standing.graceSince)).getTime()
  );
}

function ceilingOf(tier: Tier): string {
  return (PLANS[tier].fairUsePerMonth ?? 0).toLocaleString("en-US");
}

function tierName(tier: Tier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/** The sentence a fair-use degrade owes: when it ends, and what the next tier allows. */
export function fairUseSentence(tier: Tier, now: Date): string {
  const resets = `On ${dayName(windowResetsOn(now))} the count resets and your pages come back.`;
  const passed = `This month's scans passed ${tierName(tier)}'s fair-use ceiling of ${ceilingOf(tier)}.`;
  return tier === "launch"
    ? `${passed} ${resets} Starter includes ${ceilingOf("starter")} scans a month.`
    : `${passed} ${resets} Growth and above are never switched over for scan volume.`;
}

export interface BillingAttention {
  readonly kind: "billing-grace" | "billing-degraded";
  readonly title: string;
  readonly body: string;
}

/**
 * The attention card's billing row, or `null`. Churned has none — nothing is owed, and a
 * neutral row would title the card "waiting on us" — Drift Log §U60.
 */
export function billingAttention(
  standing: BillingStanding,
  now: Date,
): BillingAttention | null {
  if (standing.billingStatus === "grace" && standing.graceSince !== null) {
    const deadline = dayName(graceEndsAt(new Date(standing.graceSince)));
    return {
      kind: "billing-grace",
      title: `A payment did not go through on ${dayName(new Date(standing.graceSince))}`,
      body:
        `Nothing has changed for anyone scanning your products, and nothing will before ` +
        `${deadline}. After that, scans land on our version of your pages until the payment ` +
        `goes through. Billing & usage has the card update.`,
    };
  }
  if (standing.billingStatus === "degraded") {
    const payment = degradedByPayment(standing, now);
    return {
      kind: "billing-degraded",
      title: "Your codes are answering on our pages",
      body:
        `Every printed code still resolves and machine requests are unchanged. Your ` +
        `branding is paused ` +
        (payment
          ? `until the outstanding payment goes through.`
          : `until the scan count resets on ${dayName(windowResetsOn(now))}.`) +
        ` Billing & usage says what brings it back.`,
    };
  }
  return null;
}
